/* eslint-disable react/prop-types */
/**
 * TRS lookups for the venue forms — Township / Range / Section picked from the
 * real survey grid instead of typed from memory.
 *
 * Typing a TRS by hand is where the legacy Access tracker collected its worst
 * data: transposed digits, "8N" vs "08N", sections that don't exist in the
 * township. These controls turn the TRS fields into lookups.
 *
 * TWO CONTROLS, BECAUSE THE TWO VENUE TABLES STORE TRS DIFFERENTLY
 * ---------------------------------------------------------------
 * - `lease_deed_tracts` (migration 016) has THREE columns — `township`,
 *   `range`, `section` — plus a generated `legalstr`. Rendered by `TrsSelect`,
 *   one instance per field, wired through `TRS_FIELD_RENDERERS`.
 * - `well_tracts` (migration 018) has ONE free-text column, `trs`, labelled
 *   "Township-Range-Section" by the metadata (migration 028). Rendered by
 *   `TrsCompositeSelect` — the same three lookups stacked in one field —
 *   wired through `trsFieldRenderer`.
 *
 * OPTION SOURCE
 * -------------
 * GET /api/spatial/plss/options — distinct values from de-hub's `plss_sections`
 * table, the same grid the map draws its section polygons from. Each list is
 * scoped by the levels above it: townships by state+county, ranges by those
 * plus the chosen township, sections by those plus the chosen range. So the
 * sections offered are the ones that actually exist in that township-range,
 * not a blind 1–36.
 *
 * These values are deliberately NOT in `pick_lists` (Oklahoma alone is tens of
 * thousands of sections, and the vocabulary is three levels deep where
 * pick_lists scopes to a single parent field) — which also means the API does
 * not validate writes against them. These controls constrain the UI; they are
 * not a server-side guarantee.
 *
 * FORMAT
 * ------
 * The app's TRS format is `00A-00A-00` — "07N-07W-21". The survey grid does not
 * use it: `plss_sections` carries the landgrid's `000A-000A-00`
 * ("007N-007W-21"), three digits on the township and range. Everything entering
 * a form field goes through the formatters below, so the padding of whatever
 * came out of the grid, off an old row, or off the keyboard never reaches the
 * database — where, for `lease_deed_tracts`, de-hub's `validate_trs_components`
 * would reject it outright.
 *
 * WHY AutoComplete AND NOT Select
 * -------------------------------
 * Only Oklahoma's grid is loaded today. A strict Select would leave a broker
 * working a Texas tract unable to enter anything at all, and would reject the
 * legacy rows already stored whose spelling doesn't match the grid ("8N" where
 * the grid says "07N"). AutoComplete offers the real values and still accepts a
 * typed one, so a venue with no grid data degrades to the free-text field it is
 * today instead of blocking the save.
 *
 * Wired via WigMetadataForm/WigMetadataCrudPanel `customRenderers`, which
 * render inside the panel's edit Form — so `Form.useFormInstance()` /
 * `Form.useWatch` resolve the active row's form and the wrapping `Form.Item`
 * injects `value`/`onChange`. Same contract as `countySelect.jsx`, which owns
 * the County level of the same cascade.
 */
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AutoComplete, Form, Input, Space } from "antd";

import { AppContext } from "../AppContextProvider";
import spatialService from "../services/spatialService";

// The venue entities name these fields identically across lease_deed_tracts /
// well_tracts (de-hub migrations 016 / 018), so the parent field names are
// fixed rather than read off the metadata — unlike county, whose scoping field
// the metadata names via `options_parent_field`.
const STATE_FIELD = "state";
const COUNTY_FIELD = "county";
const TOWNSHIP_FIELD = "township";
const RANGE_FIELD = "range";

const norm = (v) => (v == null ? "" : String(v).trim().toUpperCase());

// ---------------------------------------------------------------------------
// Canonical spelling — the TRS format is 00A-00A-00
// ---------------------------------------------------------------------------
// Every part is two characters of number: township and range carry their
// direction letter on the end ("07N", "07W"), the section doesn't ("21").
//
// Nothing upstream agrees with that. The survey grid carries the landgrid's own
// spelling, which pads township and range to THREE digits —
// `000A-000A-00`, "007N-007W-21" — and legacy rows and typed input can be
// anything at all. So these formatters RE-NUMBER rather than pad: they read the
// number and re-render it two wide, which lands "007N", "07N" and "7N" all on
// "07N" no matter which side they came from.
//
// Two is also what the rest of the stack already assumes: de-hub's
// `validate_trs_components` REJECTS a township or range with more than two
// digits (`^\d{1,2}[NS]$` / `^\d{1,2}[EW]$`) and a section outside 1..36, and
// `lease_deed_tracts.legalstr` is generated as
// `LPAD(township,3,'0')-LPAD(range,3,'0')-LPAD(section::text,2,'0')`, which
// renders exactly this format back out.
const PART_WIDTH = 2;

const TWP_RNG_RE = /^(\d+)([NSEW]?)$/;
const SECTION_RE = /^(\d+)$/;

const renumber = (digits) => String(parseInt(digits, 10)).padStart(PART_WIDTH, "0");

/**
 * "7n" / "007N" -> "07N". Anything that isn't digits-plus-direction (the
 * 'UNKNOWN' venue sentinel) is left alone beyond upper-casing.
 */
const fmtTwpRng = (v) => {
  const m = norm(v).match(TWP_RNG_RE);
  return m ? `${renumber(m[1])}${m[2]}` : norm(v);
};

/**
 * "3" / "003" -> "03". A number too big for the format is rendered in full
 * rather than cut — losing a digit is worse than an off-format value, and the
 * API will reject it (section 1..36) rather than store something wrong.
 */
const fmtSection = (v) => {
  const m = norm(v).match(SECTION_RE);
  return m ? renumber(m[1]) : norm(v);
};

/**
 * Per-level config: which key of the API payload the level reads, and which
 * sibling fields scope it.
 *
 * `parents` is also the clear-on-change list — changing a level invalidates
 * everything below it, so each level clears itself when a level above it moves.
 */
const LEVELS = {
  township: {
    listKey: "townships",
    defaultField: TOWNSHIP_FIELD,
    defaultLabel: "Township",
    parents: [STATE_FIELD, COUNTY_FIELD],
    placeholder: "e.g. 07N",
    format: (v) => fmtTwpRng(v),
  },
  range: {
    listKey: "ranges",
    defaultField: RANGE_FIELD,
    defaultLabel: "Range",
    parents: [STATE_FIELD, COUNTY_FIELD, TOWNSHIP_FIELD],
    placeholder: "e.g. 07W",
    format: (v) => fmtTwpRng(v),
  },
  section: {
    listKey: "sections",
    defaultField: "section",
    defaultLabel: "Section",
    parents: [STATE_FIELD, COUNTY_FIELD, TOWNSHIP_FIELD, RANGE_FIELD],
    placeholder: "e.g. 21",
    // Two digits, the same as the composed TRS string uses. This level writes
    // `lease_deed_tracts.section`, which feeds the generated `legalstr` column
    // as `LPAD(section::text, 2, '0')` (migration 016) — so two is also the
    // width that survives round-tripping through legalstr, since Postgres LPAD
    // TRUNCATES anything longer.
    format: fmtSection,
  },
};

// Module-level cache of in-flight and settled requests, keyed by the query
// params. Levels that share a key share the request: with no township chosen
// all three controls ask for `OK|GRADY||` and only one call goes out. Holding
// the PROMISE rather than the result is what de-dupes the concurrent mount of
// three controls in the same form.
const _optionsCache = new Map();

const cacheKey = ({ state, county, twp, rng }) =>
  `${state || ""}|${county || ""}|${twp || ""}|${rng || ""}`;

// The options payload sits at `data.data` in the de-hub envelope; tolerate a
// bare payload too, mirroring the unwrap in useSpatialData.
const extractOptions = (resp) => {
  const body = resp?.data;
  const payload = body?.data && !Array.isArray(body.data) ? body.data : body;
  return payload && typeof payload === "object" ? payload : {};
};

/**
 * Fetch one level's option list. Returns `[{ value }]` for AutoComplete plus a
 * loading flag. Never throws — a grid that can't be reached degrades to an
 * empty list, i.e. plain free text, rather than an error on a field the user
 * can still fill in by hand.
 *
 * `format` puts the grid's raw values into the canonical 00A-00A-00 spelling
 * before they are offered, so picking from the list can't introduce an
 * off-format value. Formatting can collapse two raw values into one ("7N" and
 * "07N"), hence the de-dupe; server order is preserved because it is already
 * numeric and padding doesn't reorder.
 */
const useTrsOptions = ({ state, county, twp, rng, listKey, format }) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const params = useMemo(
    () => ({
      state,
      county: county || undefined,
      twp: twp || undefined,
      rng: rng || undefined,
    }),
    [state, county, twp, rng]
  );

  useEffect(() => {
    let cancelled = false;
    if (!state) {
      setOptions([]);
      return undefined;
    }
    const key = cacheKey(params);
    (async () => {
      setLoading(true);
      try {
        if (!_optionsCache.has(key)) {
          const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
          let tk = token;
          if (!tk || isTokenExpired(tk)) tk = await getToken();
          // Cache the promise, not the awaited value — three controls mounting
          // together must not fire three identical requests.
          _optionsCache.set(
            key,
            spatialService
              .getPlssOptions(tk, userOid, params)
              .then(extractOptions)
              .catch((err) => {
                // A failure must not poison the cache for the rest of the
                // session — drop the entry so the next open retries.
                _optionsCache.delete(key);
                throw err;
              })
          );
        }
        const payload = await _optionsCache.get(key);
        const list = Array.isArray(payload?.[listKey]) ? payload[listKey] : [];
        const canonical = [
          ...new Set(list.map((v) => (format ? format(v) : String(v)))),
        ];
        if (!cancelled) setOptions(canonical.map((v) => ({ value: v })));
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params, state, listKey, format, token, getToken, isTokenExpired, user]);

  return { options, loading };
};

/** Shared AutoComplete presentation for a single TRS level. */
const TrsAutoComplete = ({
  value,
  onChange,
  options,
  loading,
  disabled,
  placeholder,
  hasState,
  format,
  ...rest
}) => {
  const emit = (v, { canonical } = {}) => {
    if (v === undefined || v === null || v === "") return onChange?.(undefined);
    // Uppercase while typing; pad to the 00A-00A-00 spelling only once the
    // value is settled. Padding per keystroke would fight the user — a typed
    // "1" would become "01" before they got to the "0" of "10N".
    return onChange?.(canonical && format ? format(v) : norm(v));
  };

  return (
    <AutoComplete
      value={value ?? undefined}
      onChange={(v) => emit(v)}
      // Picked from the list: already canonical (useTrsOptions formats the
      // options), but run through the same path so the two agree.
      onSelect={(v) => emit(v, { canonical: true })}
      onBlur={() => emit(value, { canonical: true })}
      options={options}
      disabled={disabled}
      allowClear
      // Substring match, so typing "7" finds "07N" — the leading zero is the
      // thing users most often leave off.
      filterOption={(input, option) =>
        String(option?.value || "").includes(norm(input))
      }
      placeholder={hasState ? placeholder : "Select a state first"}
      notFoundContent={
        loading
          ? "Loading…"
          : !hasState
          ? "Select a state first"
          : "No survey grid for this venue — type a value"
      }
      {...rest}
    />
  );
};

/**
 * One TRS level bound to its own column (`lease_deed_tracts`). Reads the
 * levels above it off the sibling form fields.
 */
export const TrsSelect = ({ value, onChange, disabled, row, level = "township" }) => {
  const cfg = LEVELS[level] || LEVELS.township;
  const form = Form.useFormInstance();

  const state = norm(Form.useWatch(STATE_FIELD, form));
  const county = norm(Form.useWatch(COUNTY_FIELD, form));
  const township = norm(Form.useWatch(TOWNSHIP_FIELD, form));
  const range = norm(Form.useWatch(RANGE_FIELD, form));

  // Only the levels ABOVE this one narrow the query — a level must not filter
  // its own list by its own current value, or the dropdown collapses to the
  // value already chosen and the user can never change it.
  const { options, loading } = useTrsOptions({
    state,
    county,
    twp: cfg.parents.includes(TOWNSHIP_FIELD) ? township : undefined,
    rng: cfg.parents.includes(RANGE_FIELD) ? range : undefined,
    listKey: cfg.listKey,
    format: cfg.format,
  });

  // Clear this level when a level ABOVE it changes to a different value — a
  // section is meaningless once its township moves.
  const byField = {
    [STATE_FIELD]: state,
    [COUNTY_FIELD]: county,
    [TOWNSHIP_FIELD]: township,
    [RANGE_FIELD]: range,
  };
  const parentSig = cfg.parents.map((f) => byField[f]).join("|");
  const prevSigRef = useRef(parentSig);
  useEffect(() => {
    const prev = prevSigRef.current;
    // A signature of nothing but separators is the blank first render, not a
    // prior selection: `initialValues` land a tick after mount, so treating it
    // as a change would wipe the saved TRS every time an existing row opens.
    const prevHadParents = prev.split("|").some(Boolean);
    if (prevHadParents && prev !== parentSig && value) onChange?.(undefined);
    prevSigRef.current = parentSig;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentSig]);

  return (
    <TrsAutoComplete
      value={value}
      onChange={onChange}
      options={options}
      loading={loading}
      disabled={disabled}
      hasState={Boolean(state)}
      placeholder={cfg.placeholder}
      format={cfg.format}
      aria-label={row?.label || cfg.defaultLabel}
    />
  );
};

// ---------------------------------------------------------------------------
// Composite control — one field holding the whole TRS (`well_tracts.trs`)
// ---------------------------------------------------------------------------

/**
 * The stored TRS format: `00A-00A-00` — "07N-07W-21". Two digits plus direction
 * for the township and range, two digits for the section. Identical to what
 * `lease_deed_tracts.legalstr` renders for the same location, so the two venue
 * tables spell a TRS the same way despite storing it differently.
 *
 * This is NOT the grid's spelling. `plss_sections.trs` is the landgrid's
 * `000A-000A-00` ("007N-007W-21"), and `properties.trs_key` on
 * `/plss/sections` is built from the same raw columns — both three wide on the
 * township and range. Match on the parts (via `parseTrs`), not by comparing the
 * strings.
 */
const composeTrs = (twp, rng, sec) =>
  twp && rng && sec
    ? `${fmtTwpRng(twp)}-${fmtTwpRng(rng)}-${fmtSection(sec)}`
    : undefined;

/**
 * Split a stored TRS into its three parts, canonicalising each. Tolerant on the
 * way in — "7N-7W-21" parses — because the padding is what this control is here
 * to add; strict on the shape, returning `null` for anything that isn't three
 * non-empty dash-separated tokens. Legacy rows loaded from the Excel template
 * may spell TRS another way entirely, and those must not be reformatted.
 */
export const parseTrs = (value) => {
  const parts = norm(value).split("-");
  if (parts.length !== 3 || parts.some((p) => !p)) return null;
  return {
    twp: fmtTwpRng(parts[0]),
    rng: fmtTwpRng(parts[1]),
    sec: fmtSection(parts[2]),
  };
};

export const TrsCompositeSelect = ({ value, onChange, disabled, row }) => {
  const form = Form.useFormInstance();
  const state = norm(Form.useWatch(STATE_FIELD, form));
  const county = norm(Form.useWatch(COUNTY_FIELD, form));

  const parsed = parseTrs(value);
  // A stored value we can't split into three tokens gets a plain text box, so
  // opening an old row can never rewrite or drop what's already there. Once
  // it's cleared, the lookups come back.
  const unparseable = Boolean(value) && parsed === null;

  const [parts, setParts] = useState(() => parsed || { twp: "", rng: "", sec: "" });

  // Re-sync when the form loads a different row into the same mounted control
  // (the panel reuses one form instance across rows).
  const lastValueRef = useRef(value);
  useEffect(() => {
    if (value !== lastValueRef.current) {
      lastValueRef.current = value;
      setParts(parseTrs(value) || { twp: "", rng: "", sec: "" });
    }
  }, [value]);

  // Moving the venue invalidates the whole TRS, same rule the three-column
  // control applies per level. Guarded against the blank first render so
  // `initialValues` landing a tick after mount doesn't wipe a saved TRS.
  const venueSig = `${state}|${county}`;
  const prevVenueRef = useRef(venueSig);
  useEffect(() => {
    const prev = prevVenueRef.current;
    const prevHadVenue = prev.split("|").some(Boolean);
    if (prevHadVenue && prev !== venueSig && value) {
      setParts({ twp: "", rng: "", sec: "" });
      lastValueRef.current = undefined;
      onChange?.(undefined);
    }
    prevVenueRef.current = venueSig;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueSig]);

  const twpOpts = useTrsOptions({
    state,
    county,
    listKey: "townships",
    format: fmtTwpRng,
  });
  const rngOpts = useTrsOptions({
    state,
    county,
    twp: parts.twp,
    listKey: "ranges",
    format: fmtTwpRng,
  });
  const secOpts = useTrsOptions({
    state,
    county,
    twp: parts.twp,
    rng: parts.rng,
    listKey: "sections",
    format: fmtSection,
  });

  const setPart = (key) => (v) => {
    const next = { ...parts, [key]: norm(v) };
    // Changing a level invalidates the levels below it.
    if (key === "twp") {
      next.rng = "";
      next.sec = "";
    } else if (key === "rng") {
      next.sec = "";
    }
    setParts(next);
    // Only emit a complete TRS — a half-picked one would be stored as a
    // partial string and read back as a real location.
    const composed = composeTrs(next.twp, next.rng, next.sec);
    lastValueRef.current = composed;
    onChange?.(composed);
  };

  if (unparseable) {
    return (
      <Input
        value={value}
        onChange={(e) => onChange?.(e.target.value ? norm(e.target.value) : undefined)}
        disabled={disabled}
        allowClear
        aria-label={row?.label || "TRS"}
        // Clearing it swaps this back to the three lookups.
        placeholder="Township-Range-Section"
      />
    );
  }

  // `format` per part canonicalises what is DISPLAYED once each box settles;
  // composeTrs applies the same formatters to what is STORED, so the boxes and
  // the field value can't drift apart. Widths match the standalone columns'
  // (00A / 00A / 00), so the two venue tables spell a TRS identically.
  return (
    <Space.Compact style={{ width: "100%" }}>
      <TrsAutoComplete
        value={parts.twp || undefined}
        onChange={setPart("twp")}
        options={twpOpts.options}
        loading={twpOpts.loading}
        disabled={disabled}
        hasState={Boolean(state)}
        placeholder="Twp"
        format={fmtTwpRng}
        aria-label="Township"
        style={{ width: "34%" }}
      />
      <TrsAutoComplete
        value={parts.rng || undefined}
        onChange={setPart("rng")}
        options={rngOpts.options}
        loading={rngOpts.loading}
        disabled={disabled}
        hasState={Boolean(state)}
        placeholder="Rng"
        format={fmtTwpRng}
        aria-label="Range"
        style={{ width: "33%" }}
      />
      <TrsAutoComplete
        value={parts.sec || undefined}
        onChange={setPart("sec")}
        options={secOpts.options}
        loading={secOpts.loading}
        disabled={disabled}
        hasState={Boolean(state)}
        placeholder="Sec"
        format={fmtSection}
        aria-label="Section"
        style={{ width: "33%" }}
      />
    </Space.Compact>
  );
};

// ---------------------------------------------------------------------------
// customRenderers factories — each renders its own Form.Item per the
// WigMetadataForm contract ("consumer renders their own Form.Item"), same as
// `countyFieldRenderer`.
// ---------------------------------------------------------------------------
// Named rather than an arrow: these are render functions called as
// `customRenderers[field](row)`, not components React mounts, and the naming
// is what keeps react/display-name from reading them as anonymous components.
const makeFieldRenderer = (defaultField, defaultLabel, renderControl) =>
  function trsFormItemRenderer(row) {
    const field = row?.field || defaultField;
    const label = row?.label || defaultLabel;
    return (
      <Form.Item
        key={field}
        name={field}
        label={label}
        rules={
          row?.required
            ? [{ required: true, message: `${label} is required` }]
            : undefined
        }
      >
        {renderControl(row)}
      </Form.Item>
    );
  };

const makeLevelRenderer = (level) =>
  makeFieldRenderer(LEVELS[level].defaultField, LEVELS[level].defaultLabel, (row) => (
    <TrsSelect row={row} level={level} />
  ));

export const townshipFieldRenderer = makeLevelRenderer("township");
export const rangeFieldRenderer = makeLevelRenderer("range");
export const sectionFieldRenderer = makeLevelRenderer("section");

/** The single-column TRS field on `well_tracts`. */
export const trsFieldRenderer = makeFieldRenderer("trs", "TRS", (row) => (
  <TrsCompositeSelect row={row} />
));

/**
 * Spread into a panel's `customRenderers` alongside `county: countyFieldRenderer`
 * to turn the whole venue block into one cascade:
 * state → county → township → range → section.
 *
 * Carries both shapes: a panel whose metadata has the three columns picks up
 * `township`/`range`/`section`, one whose metadata has the single column picks
 * up `trs`, and the keys that don't match any field are simply never called.
 */
export const TRS_FIELD_RENDERERS = {
  township: townshipFieldRenderer,
  range: rangeFieldRenderer,
  section: sectionFieldRenderer,
  trs: trsFieldRenderer,
};

export default TrsSelect;
